// src/pages/PairPage.js
import React, { useMemo, useState, useEffect, useRef } from "react";
import { Box, Button, OutlinedInput, Typography, Alert, Link } from "@mui/material";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../firebase";
import { normalizeCode } from "../lib/pairingCode";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import BrandLogo from "../components/BrandLogo";

const CODE_LEN = 6;
const PAIR_QS_KEY = "__PAIR_QS__";

function splitToDigits(str) {
  const s = String(str || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, CODE_LEN);
  return Array.from({ length: CODE_LEN }, (_, i) => s[i] || "");
}

export default function PairPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchQS = location.search || "";
  const params = useMemo(() => new URLSearchParams(searchQS), [searchQS]);
  const hasCodeParam = useMemo(() => params.has("code"), [params]);

  // Cache the current pairing QS while we’re on /pair
  useEffect(() => {
    if (hasCodeParam) {
      try {
        sessionStorage.setItem(PAIR_QS_KEY, searchQS);
      } catch { }
    }
  }, [hasCodeParam, searchQS]);

  // Code from URL (for initial filling)
  const initial = (params.get("code") || "").trim().toUpperCase();

  // Controlled 6-char state
  const [digits, setDigits] = useState(() => splitToDigits(initial));
  const inputsRef = useRef(Array.from({ length: CODE_LEN }, () => React.createRef()));
  const pairBtnRef = useRef(null);

  // Auth status
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [isAuthed, setIsAuthed] = useState(!!auth.currentUser);
  const [currentEmail, setCurrentEmail] = useState(auth.currentUser?.email || "");
  const [switching, setSwitching] = useState(false);

  // Keep inputs in sync with URL ?code=...
  useEffect(() => {
    const next = splitToDigits(initial);
    setDigits(next);

    const filledCount = next.filter(Boolean).length;

    // FOCUS RULES:
    // 1) If a full code came from the URL, focus the Pair button.
    if (hasCodeParam && filledCount === CODE_LEN) {
      pairBtnRef.current?.focus();
      return;
    }

    // 2) If no code param at all, focus the first text field.
    if (!hasCodeParam) {
      const el0 = inputsRef.current[0]?.current;
      if (el0) {
        el0.focus();
        el0.select?.();
      }
      return;
    }

    // 3) If code param is present but incomplete, focus first empty position.
    const firstEmpty = next.findIndex((c) => !c);
    const idx = firstEmpty === -1 ? 0 : firstEmpty;
    const el = inputsRef.current[idx]?.current;
    if (el) {
      el.focus();
      el.select?.();
    }
  }, [initial, hasCodeParam]);

  // Track auth (no redirect here)
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setIsAuthed(!!user);
      setCurrentEmail(user?.email || user?.providerData?.[0]?.email || "");
    });
    return () => unsub();
  }, []);

  const verifyFn = useMemo(() => httpsCallable(functions, "verifyCode"), []);

  /** Helpers */
  const focusIndex = (idx) => {
    const el = inputsRef.current[idx]?.current;
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const setDigitAt = (idx, val) => {
    setDigits((prev) => {
      const next = prev.slice();
      next[idx] = val;
      return next;
    });
  };

  const codeString = digits.join("");
  const canPair = codeString.length === CODE_LEN;

  /** Input handlers */
  const handleChange = (idx, raw) => {
    const ch = String(raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 1);
    if (!ch) {
      setDigitAt(idx, "");
      return;
    }
    setDigitAt(idx, ch);
    if (idx < CODE_LEN - 1) focusIndex(idx + 1);
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace") {
      if (digits[idx]) {
        setDigitAt(idx, "");
      } else if (idx > 0) {
        setDigitAt(idx - 1, "");
        focusIndex(idx - 1);
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      focusIndex(idx - 1);
    } else if (e.key === "ArrowRight" && idx < CODE_LEN - 1) {
      focusIndex(idx + 1);
    } else if (e.key === "Enter") {
      handleVerify();
    }
  };

  // Paste support
  const distributePaste = (text) => {
    const cleaned = String(text || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (!cleaned) return;
    const next = splitToDigits(cleaned);
    setDigits(next);
    const firstEmpty = next.findIndex((c) => !c);
    const idx = firstEmpty === -1 ? CODE_LEN - 1 : firstEmpty;
    focusIndex(idx);
  };

  const handlePasteBox = (e) => {
    e.preventDefault();
    distributePaste(e.clipboardData?.getData("text") || "");
  };

  const handlePasteRow = (e) => {
    e.preventDefault();
    distributePaste(e.clipboardData?.getData("text") || "");
  };

  /** Submit pairing */
  async function handleVerify() {
    setError("");

    // If not signed in, send to sign-in and preserve full query string.
    if (!auth.currentUser) {
      const qs =
        searchQS && new URLSearchParams(searchQS).toString()
          ? searchQS
          : (canPair ? `?code=${encodeURIComponent(codeString)}` : "");
      try {
        if (qs) sessionStorage.setItem(PAIR_QS_KEY, qs);
      } catch { }
      navigate(`/signin${qs}`);
      return;
    }

    if (!canPair) return;

    setStatus("verifying");
    try {
      const normalized = normalizeCode(codeString);
      const { data } = await verifyFn({ code: normalized });
      if (!data?.ok) throw new Error(data?.message || "Verification failed");

      setStatus("success");
      setTimeout(() => {
        navigate("/interact", { replace: true, state: { paired: true } });
      }, 400);
    } catch (e) {
      setError(e.message);
      setStatus("idle");
    }
  }

  /** Switch user (safe sign-out) */
  const handleSignInAsDifferent = async (e) => {
    e.preventDefault();
    if (switching) return;
    setSwitching(true);
    try {
      const authInst = getAuth();
      try {
        await signOut(authInst);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("Sign out failed (continuing):", err);
      }

      try { localStorage.clear(); } catch { }
      try { sessionStorage.setItem(PAIR_QS_KEY, searchQS || ""); } catch { }
      try {
        if (indexedDB?.databases) {
          const dbs = await indexedDB.databases();
          await Promise.all(
            dbs.map(
              (db) =>
                db?.name &&
                new Promise((res) => {
                  const req = indexedDB.deleteDatabase(db.name);
                  req.onsuccess = req.onerror = req.onblocked = () => res();
                })
            )
          );
        } else {
          ["firebaseLocalStorageDb", "firebase-installations-database"].forEach((name) => {
            try { indexedDB.deleteDatabase(name); } catch { }
          });
        }
      } catch { }

      // Always preserve the full query string
      navigate(`/signin${searchQS}`, { replace: true });
    } finally {
      setSwitching(false);
    }
  };

  /** UI */
  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 3 }}>
      <Box sx={{ width: 420, maxWidth: "90vw" }}>
        <BrandLogo size={100} mb={8} />

        <Typography variant="h4" sx={{ mb: 2 }}>
          Pair with your TV
        </Typography>

        <Typography sx={{ mb: 3, opacity: 0.8 }}>
          Enter the 6-character code shown on your TV. Codes are not case sensitive.
        </Typography>

        {/* Code row */}
        <Box
          onPaste={handlePasteRow}
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 1.5,
            mb: 2,
            userSelect: "none",
          }}
        >
          {digits.map((val, idx) => (
            <OutlinedInput
              key={idx}
              value={val}
              inputRef={inputsRef.current[idx]}
              onChange={(e) => handleChange(idx, e.target.value)}
              onKeyDown={(e) => handleKeyDown(idx, e)}
              onPaste={handlePasteBox}
              inputProps={{
                maxLength: 1,
                inputMode: "text",
                autoComplete: "one-time-code",
                style: {
                  textAlign: "center",
                  fontSize: "1.6rem",
                  lineHeight: 1.2,
                  textTransform: "uppercase",
                },
                "aria-label": `Pair code character ${idx + 1}`,
              }}
              sx={{
                width: 56,
                height: 56,
                "& .MuiOutlinedInput-input": { p: 0 },
                borderRadius: 12,
                "& .MuiOutlinedInput-notchedOutline": { borderWidth: 2 },
              }}
            />
          ))}
        </Box>

        <Button
          fullWidth
          variant="contained"
          onClick={handleVerify}
          disabled={!canPair || status === "verifying"}
          ref={pairBtnRef}
        >
          {status === "verifying" ? "Verifying…" : "Pair"}
        </Button>

        {status === "verifying" && (
          <Alert sx={{ mt: 2 }} severity="info">
            Verifying your code…
          </Alert>
        )}

        {isAuthed && (
          <>
            <Typography align="center" sx={{ mt: 3 }}>
              You’re signed in with {currentEmail || "your account"}.{" "}
            </Typography>

            <Typography align="center" sx={{ mt: 3 }}>
              <Link
                underline="always"
                onClick={handleSignInAsDifferent}
                sx={{ cursor: switching ? "not-allowed" : "pointer", opacity: switching ? 0.7 : 1 }}
              >
                {switching ? "Signing out…" : "Sign in as a different user"}
              </Link>
            </Typography>
          </>
        )}

        {status === "success" && (
          <Alert sx={{ mt: 2 }} severity="success">
            Paired. Taking you to Interact…
          </Alert>
        )}
        {!!error && (
          <Alert sx={{ mt: 2 }} severity="error">
            {error}
          </Alert>
        )}
      </Box>
    </Box>
  );
}
